/**
 * @file CompanyReportsPage.tsx
 * @brief Company Admin → Reports hub + Phase B P0 report views.
 *
 * @details
 * `/company/reports` lists the catalog; `/company/reports/:reportId` opens a
 * table/export view. Distinct from Analytics. Related:
 * docs/product/reporting.md, features/admin-reports/AdminReportsPage.tsx.
 *
 * @author DES
 * @date 27-07-2026
 * @updated 29-07-2026 — GridBlock catalog layout
 * @updated 21-10-2026 — "Coming soon" hidden unless env.showComingSoon (DES)
 */

import { Link, useParams } from "react-router-dom";
import { FileChartColumn } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import {
  GridBand,
  GridBlock,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { env } from "@/shared/config/env";
import { cn } from "@/shared/theme/cn";
import { AdoptionReportView } from "./components/AdoptionReport";
import { EnrollmentReportView } from "./components/EnrollmentReport";
import { LanguagePairsReport } from "./components/LanguagePairsReport";
import { MeetingIntelSummaryReport } from "./components/MeetingIntelSummaryReport";
import { PoolBurnReport } from "./components/PoolBurnReport";
import { ProductMixReport } from "./components/ProductMixReport";
import { ScheduleOpsReportView } from "./components/ScheduleOpsReport";
import { SpendByProductReport } from "./components/SpendByProductReport";
import { UsageByEmployeeReport } from "./components/UsageByEmployeeReport";
import {
  COMPANY_REPORT_CATALOG,
  getCompanyReport,
} from "./reportCatalog";

function ReportsCatalog() {
  const available = COMPANY_REPORT_CATALOG.filter((r) => r.available);
  const upcoming = env.showComingSoon ? COMPANY_REPORT_CATALOG.filter((r) => !r.available) : [];

  return (
    <div className={gridPageBleedClass("pb-6")}>
      <GridBlock>
        <GridBand>
          <h2 className="text-xl font-semibold tracking-tight">Reports</h2>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            Date-ranged, exportable tables for your company. Use Analytics for
            charts.
          </p>
        </GridBand>

        {/* "Available" only means something beside "Coming soon". */}
        {upcoming.length > 0 ? (
          <GridBand className="py-2.5">
            <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Available
            </h3>
          </GridBand>
        ) : null}

        <GridCells className="sm:grid-cols-2 xl:grid-cols-3">
          {available.map((report) => {
            const Icon = report.icon;
            return (
              <GridCell key={report.id} padded={false}>
                <Link
                  to={`/company/reports/${report.id}`}
                  className={cn(
                    "group flex h-full flex-col p-4 transition-colors md:p-5",
                    "hover:bg-muted/30",
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex h-9 items-center text-brand">
                      <Icon className="h-5 w-5" />
                    </div>
                    <Badge variant="outline">{report.priority}</Badge>
                  </div>
                  <h4 className="mt-3 font-medium group-hover:text-brand">
                    {report.title}
                  </h4>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {report.description}
                  </p>
                </Link>
              </GridCell>
            );
          })}
        </GridCells>

        {upcoming.length > 0 ? (
          <>
            <GridBand className="py-2.5">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Coming soon
              </h3>
            </GridBand>
            <GridCells className="sm:grid-cols-2 xl:grid-cols-3">
              {upcoming.map((report) => {
                const Icon = report.icon;
                return (
                  <GridCell
                    key={report.id}
                    className="opacity-70"
                    padded={false}
                  >
                    <div className="flex h-full flex-col p-4 md:p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex h-9 items-center text-muted-foreground">
                          <Icon className="h-5 w-5" />
                        </div>
                        <Badge variant="muted">{report.priority}</Badge>
                      </div>
                      <h4 className="mt-3 text-base font-medium">
                        {report.title}
                      </h4>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {report.description}
                      </p>
                    </div>
                  </GridCell>
                );
              })}
            </GridCells>
          </>
        ) : null}
      </GridBlock>
    </div>
  );
}

export function CompanyReportsPage() {
  const { reportId } = useParams<{ reportId?: string }>();

  if (!reportId) {
    return <ReportsCatalog />;
  }

  const meta = getCompanyReport(reportId);
  if (!meta || !meta.available) {
    return (
      <div className={gridPageBleedClass("pb-6")}>
        <GridBlock>
          <GridBand>
            <Link
              to="/company/reports"
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              ← All reports
            </Link>
          </GridBand>
          <GridCell>
            <div className="flex items-center gap-2 text-base font-semibold">
              <FileChartColumn className="h-4 w-4" />
              Report not available
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              This report id is unknown or not shipped yet.
            </p>
          </GridCell>
        </GridBlock>
      </div>
    );
  }

  switch (meta.id) {
    case "co-usage-employee":
      return <UsageByEmployeeReport />;
    case "co-spend-invoices":
      return <SpendByProductReport />;
    case "co-meeting-intel":
      return <MeetingIntelSummaryReport />;
    case "co-pool-burn":
      return <PoolBurnReport />;
    case "co-product-mix":
      return <ProductMixReport />;
    case "co-language-pairs":
      return <LanguagePairsReport />;
    case "co-schedule-ops":
      return <ScheduleOpsReportView />;
    case "co-enrollment":
      return <EnrollmentReportView />;
    case "co-adoption":
      return <AdoptionReportView />;
    default:
      return <ReportsCatalog />;
  }
}
